<script setup lang="ts">
import { computed } from 'vue';
import { NxtIcon } from '@future-harry/nxt-styles';
import { useJobBoardStore } from '@/core/collections/job-boards/job-board.store';

const jobBoardStore = useJobBoardStore();
const jobBoard = computed(() => jobBoardStore.currentJobBoard);
const activeLanguage = computed(() => jobBoardStore.activeLanguageName);

const viewport = inject<'desktop' | 'tablet' | 'mobile'>('viewport');

const socialIconMap: Record<string, string> = {
   facebook: 'fab fa-square-facebook',
   linkedin: 'fab fa-linkedin',
   instagram: 'fab fa-instagram',
   x: 'fab fa-x-twitter',
};

const filteredSocialLinks = computed(() => {
   if (!jobBoard.value?.social_urls) return [];
   return Object.entries(jobBoard.value.social_urls)
      .filter(([_, url]) => url)
      .map(([platform, url]) => ({ platform, url }));
});

const privacyPolicyUrl = computed(() => {
   if (!jobBoard.value?.privacy_policy) return null;
   return jobBoard.value.privacy_policy[activeLanguage.value] || null;
});

const cookiePolicyUrl = computed(() => {
   if (!jobBoard.value?.cookie_policy) return null;
   return jobBoard.value.cookie_policy[activeLanguage.value] || null;
});

const termsOfServiceUrl = computed(() => {
   if (!jobBoard.value?.terms_of_service) return null;
   return jobBoard.value.terms_of_service[activeLanguage.value] || null;
});
</script>

<template>
   <footer :class="['footer-content', { 'footer-content--mobile': viewport === 'mobile' }]">
      <div class="flex-row flex-row--contact">
         <div>
            <img v-if="jobBoard?.logo_image_url" class="logo" :src="jobBoard.logo_image_url" :alt="jobBoard?.name" />
            <h3 v-if="jobBoard?.name">{{ jobBoard.name }}</h3>
            <div v-if="jobBoard?.address">
               <p v-if="jobBoard.address.street">{{ jobBoard.address.street }} {{ jobBoard.address.house_number }}</p>
               <p v-if="jobBoard.address.city">{{ jobBoard.address.city.postal_code }} {{ jobBoard.address.city.name }}</p>
               <p v-if="jobBoard?.address?.phone_number">{{ jobBoard.address.phone_number }}</p>
               <p v-if="jobBoard?.contact_email">{{ jobBoard.contact_email }}</p>
            </div>
         </div>
         <div class="socials" v-if="filteredSocialLinks.length">
            <a v-for="link in filteredSocialLinks" :key="link.platform" :href="link.url" target="_blank" rel="noopener noreferrer">
               <NxtIcon :name="socialIconMap[link.platform]" />
            </a>
         </div>
      </div>
      <div class="flex-row flex-row--powered-by">
         <div class="policies">
            <a v-if="privacyPolicyUrl" :href="privacyPolicyUrl" class="link">
               {{ $tb('job-board.settings.privacy-policy') }}
            </a>
            <a v-if="cookiePolicyUrl" :href="cookiePolicyUrl" class="link">
               {{ $tb('job-board.settings.cookie-policy') }}
            </a>
            <a v-if="termsOfServiceUrl" :href="termsOfServiceUrl" class="link">
               {{ $tb('job-board.settings.terms-and-conditions') }}
            </a>
         </div>
         <div>
            <p>
               <small>{{ $tb('footer.powered-by') }}</small>
            </p>
            <img width="100px" src="/powered-by-nxt.png" alt="NXT Logo" />
         </div>
      </div>
   </footer>
</template>

<style scoped lang="scss">
.footer-content {
   max-width: 1000px;
   margin: 0 auto;
   padding: var(--nxt-gutter-large);

   h3,
   p {
      margin: 0;
   }

   h3 {
      margin: var(--nxt-gutter) 0;
   }

   .socials,
   .policies {
      display: flex;
      gap: var(--nxt-gutter);
   }

   .flex-row {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: var(--nxt-gutter-large);

      &--powered-by {
         text-align: right;
         margin: var(--nxt-gutter-large) 0;

         small {
            padding-right: 10px;
         }
      }
   }

   .logo {
      width: 30px;

      img {
         width: 100%;
         object-fit: contain;
      }
   }

   @media (max-width: 768px) {
      .flex-row--powered-by {
         flex-direction: column;
         align-items: flex-start;
         text-align: left;

         small {
            padding-right: 0;
         }
      }
   }

   @mixin mobile-layout {
      .flex-row--contact {
         flex-wrap: wrap;

         > div:first-child {
            flex-basis: 100%;
         }

         .socials {
            flex-basis: 100%;
            margin-top: var(--nxt-gutter);
         }
      }

      .flex-row--powered-by {
         flex-direction: column;
         align-items: flex-start;
         text-align: left;

         small {
            padding-right: 0;
         }
      }

      .policies {
         flex-direction: column;
      }
   }

   @media (max-width: 400px) {
      @include mobile-layout;
   }

   &.footer-content--mobile {
      @include mobile-layout;
   }
}
</style>
